import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { blobResponseConfig, ensureExcelBlob, postMultipartFile } from '@/utils/blobApiUtils'
import type {
  INhomKhachHang,
  INhomKhachHangCreate,
  INhomKhachHangImportRow,
  INhomKhachHangUpdate,
} from '@/interfaces/nhomKhachHangInterfaces'
import { normalizeListResponse } from '@/utils/apiResponseUtils'

const PREFIX = '/nhom-khach-hang'

function mapRow(raw: Record<string, unknown>): INhomKhachHang {
  return {
    id: String(raw.id ?? ''),
    ma: String(raw.ma ?? ''),
    tenNhom: String(raw.tenNhom ?? raw.ten ?? ''),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai: raw.trangThai != null ? String(raw.trangThai) : undefined,
    createdAt:
      raw.ngayTao != null ? String(raw.ngayTao) : raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt:
      raw.ngayCapNhat != null
        ? String(raw.ngayCapNhat)
        : raw.updatedAt != null
          ? String(raw.updatedAt)
          : undefined,
  }
}

export function getDanhSachNhomKhachHang() {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-nhom-khach-hang`).then((res: unknown) => {
      const { data } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data.map((r) => mapRow(r as Record<string, unknown>)) }
    }),
  )
}

export function taoNhomKhachHang(body: INhomKhachHangCreate) {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-nhom-khach-hang`, {
      ma: body.ma.trim(),
      tenNhom: body.tenNhom.trim(),
      ghiChu: body.ghiChu?.trim() || undefined,
    }),
  )
}

export function capNhatNhomKhachHang(id: string, body: INhomKhachHangUpdate) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-nhom-khach-hang`, {
      ...(body.ma !== undefined ? { ma: body.ma.trim() } : {}),
      tenNhom: body.tenNhom.trim(),
      ghiChu: body.ghiChu?.trim() || undefined,
    }),
  )
}

export function xoaNhomKhachHang(id: string) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-nhom-khach-hang`))
}

export type XoaNhieuNhomKhachHangResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều nhóm KH — gọi `DELETE .../xoa-nhom-khach-hang` từng id (không có API bulk). */
export async function xoaNhieuNhomKhachHang(ids: string[]): Promise<XoaNhieuNhomKhachHangResult> {
  const failed: XoaNhieuNhomKhachHangResult['failed'] = []
  let ok = 0
  for (const id of ids) {
    try {
      await xoaNhomKhachHang(id)
      ok += 1
    } catch (e) {
      failed.push({
        id: String(id),
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiNhomKhachHang(
  id: string,
  trangThai: 'dangHoatDong' | 'ngungHoatDong',
) {
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-nhom-khach-hang`, { trangThai }),
  )
}

export type NhomKhachHangImportResult = {
  success: boolean
  message: string
  created?: number
  updated?: number
  data?: unknown[]
}

export async function taiFileMauNhapNhomKhachHangBlob(): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-file-mau-nhap-nhom-khach-hang`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export function nhapDanhSachNhomKhachHang(rows: INhomKhachHangImportRow[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/nhom-khach-hang-nhap-danh-sach`, rows))
}

export function nhapNhomKhachHangTuExcel(file: File) {
  return apiRequest(postMultipartFile(`${PREFIX}/nhap-nhom-khach-hang-tu-excel`, file))
}
